import { action } from '@ember/object';
import Service from '@ember/service';
import { service } from '@ember/service';
import Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type SessionService from './session';
import CompanyModel from 'bcl/models/company';
import LocationModel from 'bcl/models/location';
import type StocktakingModel from 'bcl/models/stocktaking';
import StocktakingEntryModel from 'bcl/models/stocktaking-entry';
import { task, all, timeout, TaskInstance } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { trackedArray } from '@ember/reactive/collections';

export default class StocktakingService extends Service {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare session: SessionService;

  get all() {
    return this.store.peekAll('stocktaking');
  }

  @action async testConnection() {
    try {
      const resp = await fetch('/api/sessions/test', {
        signal: AbortSignal.timeout(5000),
      });
      return resp.ok;
    } catch (e) {
      console.log(e);
    }
    return false;
  }
  @tracked declare stocktaking: StocktakingModel | null;
  @tracked oldChanges: StocktakingEntryModel[] = trackedArray([]);
  @tracked promises: TaskInstance<void>[] = [];
  get stillToSave() {
    return this.oldChanges.length;
  }
  @action
  _updateChanges() {
    const changes =
      this.stocktaking?.stocktakingEntries.filter(
        (ste: StocktakingEntryModel) =>
          ste.hasDirtyAttributes && ste.hasSetAmount
      ) ?? [];
    const newChanges = changes.filter((c) => !this.oldChanges.includes(c));
    newChanges.forEach((ste: StocktakingEntryModel) => {
      if (!ste.changed_at) {
        ste.changed_at = new Date();
      }
    });
    newChanges.forEach((c) => this.oldChanges.push(c));
    return newChanges;
  }

  @action async saveNewChanges() {
    // get changes which are not in the queue for now
    const newChanges = this._updateChanges();

    if (!newChanges.length) {
      return;
    }
    if (!(await this.testConnection())) {
      return;
    }
    const promises = newChanges.map((ste) => this.save.perform(ste));
    promises.forEach((p) => this.promises.push(p));

    await all(promises);
  }
  @action async waitForAllSaved() {
    await all(this.promises);
  }

  save = task(
    {
      maxConcurrency: 5,
      enqueue: true,
    },
    async (model: StocktakingEntryModel) => {
      // console.log('try', model.id, model.location_item.item.name);
      // const rand = Math.random() * 500;
      // await timeout(2000 + rand);
      // console.log('saveing', model.id, model.location_item.item.name);
      await model.save();
      const idx = this.oldChanges.findIndex((i) => i == model);
      this.oldChanges.splice(idx, 1);
    }
  );

  @action async startWith(loc_id: string) {
    const location = this.store.peekRecord<LocationModel>('location', loc_id);

    const stocktaking = await this.store
      .createRecord('stocktaking', {
        status: 'created',
        created_at: new Date().toISOString(),
      })
      .save();
    this._switchTo(stocktaking.id, location?.name);
  }
  @action resume(stocktakingId: string) {
    this._switchTo(stocktakingId);
  }
  @action leave() {
    this.stocktaking = null;
    this.save.cancelAll();
    this.oldChanges = trackedArray([]);
    this.promises = [];
  }
  @action enter(stocktaking: StocktakingModel) {
    this.stocktaking = stocktaking;
  }

  @action _switchTo(stocktakingId: string, name?: string) {
    this.enter(this.store.peekRecord('stocktaking', stocktakingId)!);
    if (name) {
      this.router.transitionTo('auth.stocktaking.name', stocktakingId, name);
      return;
    }
    const company = this.session.company;
    const locations = this.store.peekAll<LocationModel>('location');
    let location = locations[0];

    if (company?.default_location) {
      location = company.default_location;
    }
    this.router.transitionTo(
      'auth.stocktaking.name',
      stocktakingId,
      location?.name ?? 'Lager'
    );
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:stocktaking')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('stocktaking') declare altName: StocktakingService;`.
declare module '@ember/service' {
  interface Registry {
    stocktaking: StocktakingService;
  }
}
